import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type MouseEvent,
  type KeyboardEvent,
  type ElementType,
} from 'react';
import { cn } from '@/lib/cn';

/* ------------------------------------------------------------------ */
/*  Context                                                           */
/* ------------------------------------------------------------------ */

interface DropdownCtx {
  open: boolean;
  toggle: () => void;
  close: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}

const Ctx = createContext<DropdownCtx>({
  open: false,
  toggle: () => {},
  close: () => {},
  triggerRef: { current: null },
});

/* ------------------------------------------------------------------ */
/*  Root                                                              */
/* ------------------------------------------------------------------ */

export function DropdownMenu({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const toggle = useCallback(() => setOpen((v) => !v), []);
  const close = useCallback(() => setOpen(false), []);

  return (
    <Ctx.Provider value={{ open, toggle, close, triggerRef }}>
      <div className="relative inline-block">{children}</div>
    </Ctx.Provider>
  );
}

/* ------------------------------------------------------------------ */
/*  Trigger                                                           */
/* ------------------------------------------------------------------ */

export function DropdownTrigger({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const { toggle, open, triggerRef } = useContext(Ctx);

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <button
      ref={triggerRef as React.RefObject<HTMLButtonElement>}
      type="button"
      onClick={toggle}
      onKeyDown={onKeyDown}
      aria-haspopup="menu"
      aria-expanded={open}
      className={cn(
        'inline-flex items-center justify-center rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-500 focus-visible:ring-offset-1',
        'dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200 dark:focus-visible:ring-offset-slate-900',
        open && 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
        className,
      )}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Content                                                           */
/* ------------------------------------------------------------------ */

export function DropdownContent({
  children,
  align = 'end',
  className,
}: {
  children: ReactNode;
  align?: 'start' | 'end';
  className?: string;
}) {
  const { open, close, triggerRef } = useContext(Ctx);
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({});

  // Position the menu using fixed coordinates so it escapes overflow:hidden containers
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < 220;

    const pos: React.CSSProperties = {
      position: 'fixed',
      zIndex: 9999,
    };

    if (openAbove) {
      pos.bottom = window.innerHeight - rect.top + 4;
    } else {
      pos.top = rect.bottom + 4;
    }

    if (align === 'end') {
      pos.right = window.innerWidth - rect.right;
    } else {
      pos.left = rect.left;
    }

    setStyle(pos);
  }, [open, triggerRef, align]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, close, triggerRef]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, close, triggerRef]);

  // Focus first item when menu opens
  useEffect(() => {
    if (!open || !menuRef.current) return;
    const first = menuRef.current.querySelector<HTMLElement>('[role="menuitem"]');
    first?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-orientation="vertical"
      style={style}
      className={cn(
        'min-w-[180px] rounded-lg border border-slate-200 bg-white py-1 shadow-lg shadow-slate-200/60',
        'dark:border-slate-700 dark:bg-slate-800 dark:shadow-[0_8px_24px_rgba(0,0,0,0.4)]',
        'animate-dropdown-in',
        className,
      )}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Item                                                              */
/* ------------------------------------------------------------------ */

export function DropdownItem({
  icon: Icon,
  label,
  onClick,
  destructive = false,
  disabled = false,
  className,
}: {
  icon?: ElementType;
  label: string;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  const { close } = useContext(Ctx);

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (disabled) return;
    onClick();
    close();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (e.currentTarget.nextElementSibling as HTMLElement | null);
      if (next?.getAttribute('role') === 'menuitem') next.focus();
      else {
        // skip separators
        const afterNext = next?.nextElementSibling as HTMLElement | null;
        if (afterNext?.getAttribute('role') === 'menuitem') afterNext.focus();
      }
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (e.currentTarget.previousElementSibling as HTMLElement | null);
      if (prev?.getAttribute('role') === 'menuitem') prev.focus();
      else {
        const beforePrev = prev?.previousElementSibling as HTMLElement | null;
        if (beforePrev?.getAttribute('role') === 'menuitem') beforePrev.focus();
      }
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e as unknown as MouseEvent<HTMLButtonElement>);
    }
  };

  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={onKeyDown}
      className={cn(
        'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
        'focus:bg-slate-50 focus:outline-none dark:focus:bg-slate-700',
        destructive
          ? 'text-red-600 hover:bg-red-50 focus:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 dark:focus:bg-red-900/30'
          : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700',
        disabled && 'cursor-not-allowed opacity-40',
        className,
      )}
    >
      {Icon ? <Icon className={cn('h-4 w-4 flex-shrink-0', destructive ? 'text-red-500 dark:text-red-400' : 'text-slate-400 dark:text-slate-500')} /> : null}
      <span>{label}</span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Separator                                                         */
/* ------------------------------------------------------------------ */

export function DropdownSeparator() {
  return <div role="separator" className="my-1 h-px bg-slate-100 dark:bg-slate-700" />;
}
